import { fetchCustomSchemas } from '@/composables/custom-data/customData';

export interface CustomEntityProperty {
  name: string;
  label?: string;
  value: string;
  type: string;
  variableName?: string;
}

export interface CustomEntitySchema {
  schemaId: string;
  schemaName: string;
  properties: CustomEntityProperty[];
}

/**
 * Get custom properties related to a specific entity type
 * @param entityType - The system entity type (e.g., 'customers', 'orders', 'carts', 'products')
 * @returns Array of custom schemas with their properties
 */
export const getCustomEntityProperties = async (
  entityType: 'contacts' | 'orders' | 'carts' | 'products',
): Promise<CustomEntitySchema[]> => {
  try {
    const fetchedSchemas = await fetchCustomSchemas();

    // Filter schemas that have a relationship with the specified entity
    const entityRelatedSchemas = fetchedSchemas.filter((schema) => {
      if (!schema.relationships || !Array.isArray(schema.relationships)) {
        return false;
      }

      return schema.relationships.some(
        (rel) => rel.targetSchemaType === 'system' && rel.targetSchema === entityType,
      );
    });

    const customSchemas: CustomEntitySchema[] = [];

    // Helper function to recursively extract properties including nested relations
    const extractPropertiesRecursively = (
      schema: any,
      allSchemas: any[],
      prefix = '',
      excludeFields: string[] = [],
    ): CustomEntityProperty[] => {
      const properties: CustomEntityProperty[] = [];

      schema.fields
        .filter((field: any) => !excludeFields.includes(field.name))
        .forEach((field: any) => {
          // Check if this field has a custom relation
          const relation = schema.relationships?.find(
            (rel: any) => rel.sourceField === field.name && rel.targetSchemaType === 'custom',
          );

          if (relation) {
            // Find the target schema
            const targetSchema = allSchemas.find(
              (s: any) => s.id_custom_object.toString() === relation.targetSchema.toString(),
            );

            if (targetSchema) {
              // Recursively extract properties from the target schema
              // Use table_name if available, otherwise use schema_name
              const tableName = targetSchema.table_name || targetSchema.schema_name?.toLowerCase().replace(/\s+/g, '_');
              const nestedPrefix = prefix ? `${prefix}.${tableName}` : tableName;

              const nestedProperties = extractPropertiesRecursively(
                targetSchema,
                allSchemas,
                nestedPrefix,
                [],
              );
              properties.push(...nestedProperties);
            }
          } else {
            // Regular field (not a relation)
            const fieldPath = prefix ? `${prefix}.${field.name}` : field.name;
            properties.push({
              name: field.name,
              label: field.label || field.name,
              value: `${schema.id_custom_object}_${field.name}`,
              variableName: fieldPath,
              type: field.type,
            });
          }
        });

      return properties;
    };

    // Process each schema
    entityRelatedSchemas.forEach((schema) => {
      // Find entity relation fields to exclude them
      const entityRelationFields = schema.relationships
        ?.filter((rel) => rel.targetSchemaType === 'system' && rel.targetSchema === entityType)
        .map((rel) => rel.sourceField) || [];

      // Extract properties recursively, including nested relations
      // Add entity.custom prefix to the path
      const entityPrefix = entityType === 'contacts' ? 'contact' : entityType.slice(0, -1); // contacts -> contact, products -> product, orders -> order
      const fullPrefix = `${entityPrefix}.custom.${schema.table_name}`;
      const properties = extractPropertiesRecursively(
        schema,
        fetchedSchemas,
        fullPrefix,
        entityRelationFields,
      );

      customSchemas.push({
        schemaId: schema.id_custom_object?.toString() || '',
        schemaName: schema.schema_name || '',
        properties,
      });
    });

    return customSchemas;
  } catch (error) {
    console.error(`Error fetching custom ${entityType} properties:`, error);
    return [];
  }
};

/**
 * Get custom properties for orders
 */
export const getCustomOrderProperties = async (): Promise<CustomEntitySchema[]> => {
  return getCustomEntityProperties('orders');
};

/**
 * Get custom properties for carts
 */
export const getCustomCartProperties = async (): Promise<CustomEntitySchema[]> => {
  return getCustomEntityProperties('carts');
};

/**
 * Get custom properties for products
 */
export const getCustomProductProperties = async (): Promise<CustomEntitySchema[]> => {
  return getCustomEntityProperties('products');
};
